/**
 * DSA 2.0 — pipeline WebGL2 (fragment shader).
 * backend: 'webgl2'
 *
 * Texturas en RGBA8 empaquetando 16-bit (hi/lo) — compatible sin float textures.
 * R32F + LINEAR falla en muchos GPUs (lee 0 → viewport gris por offset/bias).
 */

import { hasWebGl2 } from './mpr3dWebgl'

const VS = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  // Flip Y: filas DICOM (top-first) → texcoords WebGL
  v_uv = vec2(a_pos.x * 0.5 + 0.5, 1.0 - (a_pos.y * 0.5 + 0.5));
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`

const FS = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D u_frameTexture;
uniform sampler2D u_maskTexture;
uniform sampler2D u_roadmapTexture;
uniform vec2 u_pixelShift;
uniform vec2 u_texelSize;
uniform float u_gain;
uniform float u_offset;
uniform float u_noiseFloor;
uniform float u_invert;
uniform float u_minV;
uniform float u_maxV;
uniform float u_ww;
uniform float u_wc;
uniform float u_roadmapOn;
uniform float u_roadmapAlpha;
uniform int u_roadmapLut;

in vec2 v_uv;
out vec4 fragColor;

/** Decodifica RGBA8 hi/lo → valor 0..65535 */
float sampleVal(sampler2D tex, vec2 uv) {
  vec4 c = texture(tex, uv);
  float hi = floor(c.r * 255.0 + 0.5);
  float lo = floor(c.g * 255.0 + 0.5);
  return hi * 256.0 + lo;
}

vec3 lutColor(float t, int mode) {
  t = clamp(t, 0.0, 1.0);
  if (mode == 1) return vec3(0.1, 0.95, 0.35) * t;
  if (mode == 2) return vec3(0.95, 0.2, 0.15) * t;
  return vec3(t);
}

void main() {
  vec2 shiftUv = u_pixelShift * u_texelSize;
  vec2 frameUv = v_uv;
  vec2 maskUv = v_uv - shiftUv;

  float Iframe = sampleVal(u_frameTexture, frameUv);
  float Imask = 0.0;
  if (maskUv.x >= 0.0 && maskUv.x <= 1.0 && maskUv.y >= 0.0 && maskUv.y <= 1.0) {
    Imask = sampleVal(u_maskTexture, maskUv);
  }

  float raw = Iframe - Imask;
  if (abs(raw) < u_noiseFloor) {
    raw = 0.0;
  }
  float v = raw * u_gain + u_offset;
  v = clamp(v, u_minV, u_maxV);
  if (u_invert > 0.5) {
    v = (u_minV + u_maxV) - v;
    v = clamp(v, u_minV, u_maxV);
  }

  float lo = u_wc - u_ww * 0.5;
  float gray = u_ww > 1e-3 ? clamp((v - lo) / u_ww, 0.0, 1.0) : 0.0;
  vec3 rgb = vec3(gray);

  if (u_roadmapOn > 0.5) {
    float rm = sampleVal(u_roadmapTexture, v_uv);
    float rmN = u_ww > 1e-3 ? clamp((rm - lo) / u_ww, 0.0, 1.0) : 0.0;
    vec3 tint = lutColor(rmN, u_roadmapLut);
    rgb = mix(rgb, tint, clamp(u_roadmapAlpha, 0.0, 1.0) * smoothstep(0.02, 0.15, rmN));
  }

  fragColor = vec4(rgb, 1.0);
}
`

function compile(gl, type, src) {
  const sh = gl.createShader(type)
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) || 'shader compile'
    gl.deleteShader(sh)
    throw new Error(log)
  }
  return sh
}

function link(gl, vsSrc, fsSrc) {
  const vs = compile(gl, gl.VERTEX_SHADER, vsSrc)
  const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc)
  const prog = gl.createProgram()
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog) || 'program link'
    gl.deleteProgram(prog)
    throw new Error(log)
  }
  return prog
}

function createRgbaTex(gl) {
  const tex = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, tex)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  return tex
}

/** Empaqueta valores signed/unsigned en RGBA8 (hi, lo, 0, 255). Offset +32768 si hay negativos. */
function packPixelsRgba(pixels, scratch, signedBias) {
  const n = pixels.length
  let buf = scratch
  if (!buf || buf.length !== n * 4) {
    buf = new Uint8Array(n * 4)
  }
  const bias = signedBias ? 32768 : 0
  for (let i = 0; i < n; i++) {
    let v = (pixels[i] | 0) + bias
    if (v < 0) v = 0
    if (v > 65535) v = 65535
    const o = i * 4
    buf[o] = (v >> 8) & 255
    buf[o + 1] = v & 255
    buf[o + 2] = 0
    buf[o + 3] = 255
  }
  return buf
}

/**
 * @param {HTMLCanvasElement} canvas
 */
export function createDsaGpuPipeline(canvas) {
  if (!canvas || !hasWebGl2()) return null

  let gl
  try {
    gl = canvas.getContext('webgl2', {
      alpha: false,
      depth: false,
      stencil: false,
      antialias: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    })
  } catch (e) {
    return null
  }
  if (!gl) return null

  let prog
  try {
    prog = link(gl, VS, FS)
  } catch (e) {
    console.warn('dsaGpuPipeline: link failed', e)
    return null
  }

  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW
  )

  const aPos = gl.getAttribLocation(prog, 'a_pos')
  const u = {
    frame: gl.getUniformLocation(prog, 'u_frameTexture'),
    mask: gl.getUniformLocation(prog, 'u_maskTexture'),
    roadmap: gl.getUniformLocation(prog, 'u_roadmapTexture'),
    pixelShift: gl.getUniformLocation(prog, 'u_pixelShift'),
    texelSize: gl.getUniformLocation(prog, 'u_texelSize'),
    gain: gl.getUniformLocation(prog, 'u_gain'),
    offset: gl.getUniformLocation(prog, 'u_offset'),
    noiseFloor: gl.getUniformLocation(prog, 'u_noiseFloor'),
    invert: gl.getUniformLocation(prog, 'u_invert'),
    minV: gl.getUniformLocation(prog, 'u_minV'),
    maxV: gl.getUniformLocation(prog, 'u_maxV'),
    ww: gl.getUniformLocation(prog, 'u_ww'),
    wc: gl.getUniformLocation(prog, 'u_wc'),
    roadmapOn: gl.getUniformLocation(prog, 'u_roadmapOn'),
    roadmapAlpha: gl.getUniformLocation(prog, 'u_roadmapAlpha'),
    roadmapLut: gl.getUniformLocation(prog, 'u_roadmapLut'),
  }

  const frameTex = createRgbaTex(gl)
  const maskTex = createRgbaTex(gl)
  const roadmapTex = createRgbaTex(gl)

  let lost = false
  let cols = 0
  let rows = 0
  let framePack = null
  let maskPack = null
  let roadmapPack = null
  let maskRaw = null
  let roadmapRaw = null
  let maskVersion = 0
  let uploadedMaskVersion = -1
  let roadmapVersion = 0
  let uploadedRoadmapVersion = -1
  let signedBias = false

  const onLost = (e) => {
    e.preventDefault()
    lost = true
  }
  const onRestored = () => {
    lost = true
  }
  canvas.addEventListener('webglcontextlost', onLost, false)
  canvas.addEventListener('webglcontextrestored', onRestored, false)

  function uploadTex(tex, rgba, w, h) {
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0)
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      w,
      h,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      rgba
    )
  }

  function resize(cssW, cssH) {
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const w = Math.max(1, Math.round(cssW * dpr))
    const h = Math.max(1, Math.round(cssH * dpr))
    if (canvas.width !== w) canvas.width = w
    if (canvas.height !== h) canvas.height = h
  }

  function detectSigned(pixels) {
    if (!pixels || !pixels.length) return false
    const Ctor = pixels.constructor && pixels.constructor.name
    return Ctor === 'Int16Array' || Ctor === 'Int8Array' || Ctor === 'Int32Array'
  }

  function setMaskPixels(pixels, w, h) {
    if (!pixels || !w || !h) return
    cols = w | 0
    rows = h | 0
    signedBias = detectSigned(pixels)
    maskRaw = pixels
    maskPack = packPixelsRgba(pixels, maskPack, signedBias)
    maskVersion += 1
  }

  function setRoadmapPixels(pixels, w, h) {
    if (!pixels || !w || !h) {
      roadmapVersion += 1
      roadmapRaw = null
      roadmapPack = null
      return
    }
    roadmapRaw = pixels
    roadmapPack = packPixelsRgba(pixels, roadmapPack, false)
    if (!cols) {
      cols = w | 0
      rows = h | 0
    }
    roadmapVersion += 1
  }

  function drawFrame(pixels, opts) {
    if (lost || !gl || !pixels) return false
    const w = (opts && opts.cols) | 0
    const h = (opts && opts.rows) | 0
    if (w <= 0 || h <= 0) return false
    if (!maskRaw || maskRaw.length < w * h) return false

    cols = w
    rows = h
    if (detectSigned(pixels)) signedBias = true
    framePack = packPixelsRgba(pixels, framePack, signedBias)

    try {
      uploadTex(frameTex, framePack, w, h)

      if (uploadedMaskVersion !== maskVersion) {
        maskPack = packPixelsRgba(maskRaw, maskPack, signedBias)
        uploadTex(maskTex, maskPack, w, h)
        uploadedMaskVersion = maskVersion
      }

      if (uploadedRoadmapVersion !== roadmapVersion) {
        if (roadmapRaw && roadmapRaw.length >= w * h) {
          roadmapPack = packPixelsRgba(roadmapRaw, roadmapPack, false)
          uploadTex(roadmapTex, roadmapPack, w, h)
        } else {
          const empty = new Uint8Array(w * h * 4)
          uploadTex(roadmapTex, empty, w, h)
        }
        uploadedRoadmapVersion = roadmapVersion
      }

      const minV = opts.min != null ? Number(opts.min) : 0
      const maxV = opts.max != null ? Number(opts.max) : 4095
      // Compensar bias de empaquetado signed en el shader vía offset de lectura:
      // sampleVal siempre ≥0; si Int16, los valores reales son sample - 32768.
      const packBias = signedBias ? 32768 : 0
      const ww =
        opts.ww != null && Number.isFinite(Number(opts.ww))
          ? Math.max(1, Number(opts.ww))
          : Math.max(1, maxV - minV)
      const wc =
        opts.wc != null && Number.isFinite(Number(opts.wc))
          ? Number(opts.wc)
          : (minV + maxV) / 2

      // Ajustar offset del subtract: Iframe/Imask ya vienen con +packBias → se cancelan en la resta.
      // Solo hay que asegurar min/max/voi en espacio original (sin packBias).
      void packBias

      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.disable(gl.BLEND)
      gl.useProgram(prog)
      gl.bindBuffer(gl.ARRAY_BUFFER, buf)
      gl.enableVertexAttribArray(aPos)
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)

      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, frameTex)
      gl.uniform1i(u.frame, 0)
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, maskTex)
      gl.uniform1i(u.mask, 1)
      gl.activeTexture(gl.TEXTURE2)
      gl.bindTexture(gl.TEXTURE_2D, roadmapTex)
      gl.uniform1i(u.roadmap, 2)

      gl.uniform2f(u.pixelShift, Number(opts.shiftX) || 0, Number(opts.shiftY) || 0)
      gl.uniform2f(u.texelSize, 1 / w, 1 / h)
      gl.uniform1f(u.gain, opts.gain != null ? Number(opts.gain) : 1)
      gl.uniform1f(u.offset, opts.offset != null ? Number(opts.offset) : 0)
      gl.uniform1f(u.noiseFloor, opts.noiseFloor != null ? Number(opts.noiseFloor) : 0)
      gl.uniform1f(u.invert, opts.invert ? 1 : 0)
      gl.uniform1f(u.minV, minV)
      gl.uniform1f(u.maxV, maxV)
      gl.uniform1f(u.ww, ww)
      gl.uniform1f(u.wc, wc)
      gl.uniform1f(u.roadmapOn, opts.roadmapOn ? 1 : 0)
      gl.uniform1f(
        u.roadmapAlpha,
        opts.roadmapAlpha != null ? Number(opts.roadmapAlpha) : 0.45
      )
      gl.uniform1i(u.roadmapLut, opts.roadmapLut != null ? opts.roadmapLut | 0 : 1)

      gl.drawArrays(gl.TRIANGLES, 0, 6)
      const err = gl.getError()
      if (err && err !== gl.NO_ERROR) {
        console.warn('dsaGpuPipeline GL error', err)
        return false
      }
      return true
    } catch (e) {
      console.warn('dsaGpuPipeline.drawFrame:', e)
      lost = true
      return false
    }
  }

  function destroy() {
    canvas.removeEventListener('webglcontextlost', onLost, false)
    canvas.removeEventListener('webglcontextrestored', onRestored, false)
    try {
      gl.deleteTexture(frameTex)
      gl.deleteTexture(maskTex)
      gl.deleteTexture(roadmapTex)
      gl.deleteBuffer(buf)
      gl.deleteProgram(prog)
    } catch (e) {
      /* ignore */
    }
    lost = true
  }

  return {
    backend: 'webgl2',
    isLost: () => lost || !gl || gl.isContextLost(),
    setMaskPixels,
    setRoadmapPixels,
    drawFrame,
    resize,
    destroy,
  }
}

export { hasWebGl2 }
